import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { QueuePager } from "../components/QueuePager";
import { TableControls, type TableSelectControl } from "../components/TableControls";
import { TacticDraftCard } from "../components/TacticDraftCard";
import { EmptyCard, QueryErrorState, TabHeading, TabSkeleton } from "../components/tab-states";
import { useTacticDraftsPage } from "../data/queries";

const PAGE_SIZE = 50;

/** Ranked tactic drafts awaiting an operator call. */
export function DraftsTab() {
  const [signalId, setSignalId] = useState("");
  const [market, setMarket] = useState("");
  const [tier, setTier] = useState("");
  const [offset, setOffset] = useState(0);
  const queryClient = useQueryClient();
  const page = useTacticDraftsPage({
    signal_id: signalId || undefined,
    market: market || undefined,
    tier: tier || undefined,
    limit: PAGE_SIZE,
    offset,
  });
  const items = useMemo(() => page.data?.items ?? [], [page.data]);
  // The facet options come from the CURRENT page plus whatever is selected, so a
  // selected value never vanishes from its own dropdown when it filters the page
  // down to rows that no longer contain it.
  const options = useMemo(
    () => ({
      signals: values(items.map((item) => item.signal_id), signalId),
      markets: values(items.map((item) => item.market), market),
      tiers: values(items.map((item) => item.confidence_tier), tier),
    }),
    [items, market, signalId, tier],
  );

  const selects: TableSelectControl[] = [
    { id: "signal_id", label: "Signal", value: signalId, options: options.signals },
    { id: "market", label: "Market", value: market, options: options.markets },
    { id: "tier", label: "Tier", value: tier, options: options.tiers },
  ];

  function updateSelect(id: string, value: string) {
    if (id === "signal_id") setSignalId(value);
    if (id === "market") setMarket(value);
    if (id === "tier") setTier(value);
    setOffset(0);
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["machine", "tactic-drafts"] });
  }

  const total = page.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <TabHeading
        title="Drafts"
        description="Ranked tactic drafts ready for operator review."
        aside={total ? `${total.toLocaleString("en-US")} drafts` : undefined}
      />
      {page.isPending ? (
        <TabSkeleton />
      ) : page.isError ? (
        <QueryErrorState title="Draft queue unavailable" onRetry={() => page.refetch()} />
      ) : (
        <>
          <TableControls
            selects={selects}
            textValue=""
            showText={false}
            onSelectChange={updateSelect}
            onTextChange={() => {}}
            resultCount={total}
            countNoun="drafts"
          />
          {items.length === 0 ? (
            <EmptyCard label="No drafts in queue" />
          ) : (
            <div className={`flex flex-col gap-3 ${page.isFetching ? "opacity-60" : ""}`}>
              {items.map((draft) => (
                <TacticDraftCard
                  key={draft.draft_id}
                  draft={draft}
                  onMutated={invalidate}
                  onConflict={() => void page.refetch()}
                />
              ))}
            </div>
          )}
          <QueuePager
            offset={offset}
            pageSize={PAGE_SIZE}
            total={total}
            busy={page.isFetching}
            onOffset={setOffset}
          />
        </>
      )}
    </div>
  );
}

function values(rows: string[], selected: string): Array<{ label: string; value: string }> {
  return [...new Set([...rows, selected].filter(Boolean))].sort().map((value) => ({ label: value, value }));
}
